/**
 * Área "Depoimentos": a curadoria dos depoimentos que as clientes autorizaram no Questionário
 * Avaliativo. Porte do admin atual, `carregarDepoimentos` (`portal/admin.html:3669-3713`).
 *
 * O QUE A PESSOA FAZ AQUI, igual ao legado: vê os depoimentos autorizados, mais recentes primeiro,
 * e marca "Aprovado pra uso (funil/marketing)", que grava na hora. A marca muda no clique; o
 * DESTAQUE do cartão só muda depois de o banco aceitar. Se a gravação falha, o aviso "Erro ao
 * salvar: " aparece, o cartão não se mexe, e a marca volta ao que era quando o aviso fecha.
 *
 * O RECORTE POR PAPEL: o menu só mostra esta área a administrador e consulta (`papeis.ts`, como
 * `aplicarRestricoesDePapel`, `admin.html:3433-3446`); para consulta, a marca fica travada, como o
 * `disabled` que o legado põe em todo `input` desse papel (`admin.html:3394`).
 */

// Estado e efeito do React: a lista é lida ao abrir a área, como no clique da aba do legado.
import { useCallback, useEffect, useState } from 'react';
// O cartão da plataforma: o produto não desenha superfície.
import { Cartao } from '@complexity/ui';
// O dicionário: a frase em português é a chave.
import { t } from '@complexity/i18n';
// A área de dados: a mesma consulta e a mesma gravação do legado.
import { listarDepoimentos, marcarDepoimentoAprovado, type Depoimento } from '@studiolu/dados/areas/depoimentos';
// Sessão, papel e fonte de dados de quem está no painel.
import { usePainel } from '../../contexto.js';
// O aviso de erro do legado (`mostrarAlerta`), peça comum; o achado 5 mora dentro dela.
import { useConfirmacao } from '../../comum/confirmacao.js';

/**
 * Um cartão na tela, e por que A MARCA E O DESTAQUE SÃO DUAS COISAS.
 *
 * No legado a marca é o `<input type="checkbox">` nativo: ela muda no clique, ANTES de qualquer
 * gravação. O destaque do cartão é a classe `aprovado`, e ela só muda DEPOIS do `update` sem erro
 * (`admin.html:3703-3710`): com erro, o cartão fica como estava, e é a marca que volta atrás,
 * depois que a pessoa fecha o aviso. Guardar um valor só para as duas faria o cartão acender antes
 * de o banco aceitar, e apagar durante o aviso de erro, que era o defeito medido na verificação.
 */
interface CartaoDeDepoimento {
  readonly depoimento: Depoimento;
  // O que a caixa de marcar mostra: muda no clique.
  readonly marcado: boolean;
  // O que o cartão mostra: muda só depois da gravação aceita.
  readonly destacado: boolean;
}

// Os quatro estados da lista, e nenhum outro: o legado escreve um parágrafo para cada um.
type EstadoDaLista =
  | { readonly tipo: 'carregando' }
  | { readonly tipo: 'erro'; readonly mensagem: string }
  | { readonly tipo: 'pronta'; readonly cartoes: readonly CartaoDeDepoimento[] };

export function TelaDepoimentos() {
  const { dados, acesso } = usePainel();
  // O aviso de erro de gravação.
  const { alertar, dialogo } = useConfirmacao();
  // Começa carregando: é o "Carregando…" que o legado escreve antes da consulta.
  const [lista, setLista] = useState<EstadoDaLista>({ tipo: 'carregando' });
  // Consulta trava a marca, como o `disabled` do legado para esse papel.
  const somenteLeitura = acesso.papel === 'consulta';

  useEffect(() => {
    // Protege contra gravar estado depois de a pessoa sair da área.
    let viva = true;
    void listarDepoimentos(dados).then((resultado) => {
      if (!viva) return;
      // A falha mostra a mensagem crua depois do prefixo, como o legado (`admin.html:3680`).
      if (!resultado.ok) setLista({ tipo: 'erro', mensagem: resultado.erro.mensagem });
      // Lido do banco, marca e destaque nascem iguais: é o `checked` e a classe `aprovado` que o
      // legado escreve no mesmo `map` (`admin.html:3693`, `3700`).
      else
        setLista({
          tipo: 'pronta',
          cartoes: resultado.dados.map((d) => ({ depoimento: d, marcado: d.aprovado_para_uso, destacado: d.aprovado_para_uso })),
        });
    });
    return () => {
      viva = false;
    };
  }, [dados]);

  // Marca ou desmarca: a marca muda na hora, o destaque só depois de gravar (`admin.html:3703-3711`).
  const aoMarcar = useCallback(
    async (id: number, aprovado: boolean) => {
      // Muda um cartão, lendo o estado de AGORA: durante a gravação a pessoa pode clicar de novo,
      // e o legado também lê o `cb.checked` do momento, e não o valor que mandou gravar.
      const mudarCartao = (troca: (c: CartaoDeDepoimento) => CartaoDeDepoimento) =>
        setLista((atual) =>
          atual.tipo !== 'pronta'
            ? atual
            : { ...atual, cartoes: atual.cartoes.map((c) => (c.depoimento.id === id ? troca(c) : c)) },
        );
      // A marca muda no clique, como a caixa nativa do legado.
      mudarCartao((c) => ({ ...c, marcado: aprovado }));
      const resultado = await marcarDepoimentoAprovado(dados, id, aprovado);
      if (!resultado.ok) {
        // A ordem é a do legado: primeiro o aviso, e só depois de fechado a marca volta atrás
        // (`cb.checked = !cb.checked`). O destaque do cartão NÃO se mexe em nenhum momento.
        await alertar(`Erro ao salvar: ${resultado.erro.mensagem}`);
        mudarCartao((c) => ({ ...c, marcado: !c.marcado }));
        return;
      }
      // Gravou: só agora o cartão acende ou apaga, pela marca que estiver valendo
      // (`cb.closest('.depoimento-card').classList.toggle('aprovado', cb.checked)`).
      mudarCartao((c) => ({ ...c, destacado: c.marcado }));
    },
    [dados, alertar],
  );

  // "Carregando…", "Erro ao carregar: " e o vazio: o parágrafo `.vazio` do legado.
  if (lista.tipo === 'carregando') return <p className="text-cx-sm text-texto-suave">{t('Carregando…')}</p>;
  if (lista.tipo === 'erro') {
    return <p className="text-cx-sm text-texto-suave">{`${t('Erro ao carregar:')} ${lista.mensagem}`}</p>;
  }
  if (lista.cartoes.length === 0) {
    return <p className="text-cx-sm text-texto-suave">{t('Nenhum depoimento autorizado ainda.')}</p>;
  }

  return (
    <div className="flex flex-col gap-cx-4">
      {lista.cartoes.map(({ depoimento: d, marcado, destacado }) => {
        // O nome do lead, o e-mail na falta dele, e "(sem nome)" sem lead (`admin.html:3689`).
        const nome = d.leads_funil?.nome || d.leads_funil?.email || '(sem nome)';
        // A naturalidade vem depois do nome, com o ponto médio do legado.
        const nomeExibido = d.naturalidade ? `${nome} · ${d.naturalidade}` : nome;
        return (
          // `semRespiro` tira o respiro do cartão para o miolo abaixo preenchê-lo inteiro.
          <Cartao key={d.id} semRespiro>
            {/* O cartão aprovado ganha o fundo e o contorno de sucesso, como `.depoimento-card.aprovado`
                (`admin.html:1238`). O Cartao do Chassi não tem tom, então o destaque mora no miolo, e
                o contorno é um anel interno: a borda do próprio cartão continua a dele. */}
            <div
              data-depoimento={d.id}
              data-aprovado={destacado ? 'sim' : 'nao'}
              className={`p-cx-cartao ${destacado ? 'bg-sucesso-suave ring-1 ring-inset ring-sucesso' : ''}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-cx-2">
                <span className="text-cx-base font-semibold text-texto">{nomeExibido}</span>
                {/* A nota só aparece quando existe, inclusive a nota zero (`admin.html:3691`). */}
                {d.depoimento_nota !== null && d.depoimento_nota !== undefined && (
                  <span className="whitespace-nowrap text-cx-xs font-semibold uppercase text-texto-suave">
                    {`Nota: ${d.depoimento_nota}/10`}
                  </span>
                )}
              </div>
              {/* O texto entre aspas, como texto e nunca HTML (o legado escapa, `admin.html:3698`). */}
              <p className="mt-cx-3 text-cx-lg italic text-texto">{`"${d.depoimento_texto ?? ''}"`}</p>
              {/* A marca com rótulo clicável e alvo de 44px, como `.depoimento-aprovar`. Não há peça
                  de caixa de marcar no Chassi; o `input` nativo é o que o legado usa. */}
              <label className="mt-cx-3 flex min-h-toque cursor-pointer items-center gap-cx-3 text-cx-sm text-texto">
                <input
                  type="checkbox"
                  className="h-cx-4 w-cx-4 accent-sucesso"
                  checked={marcado}
                  disabled={somenteLeitura}
                  onChange={(evento) => void aoMarcar(d.id, evento.target.checked)}
                />
                {t('Aprovado pra uso (funil/marketing)')}
              </label>
            </div>
          </Cartao>
        );
      })}
      {dialogo}
    </div>
  );
}
